/**
 * Minimal store-only (no compression) ZIP writer — dependency-free, so an .xlsx can
 * be produced entirely in the browser without a runtime CDN or a new npm dep
 * (preserves the zero-egress + minimal-supply-chain constraints, MERGE-PLAN §2/§8).
 *
 * Store method (0) means the payload is copied verbatim; a real ZIP reader (and
 * therefore Excel) accepts it. Good enough for modestly-sized evidence workbooks.
 */

const textEncoder = new TextEncoder();

// CRC-32 (IEEE 802.3) with a lazily-built lookup table.
let CRC_TABLE: Uint32Array | null = null;
function crcTable(): Uint32Array {
  if (CRC_TABLE) return CRC_TABLE;
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  CRC_TABLE = t;
  return t;
}

function crc32(bytes: Uint8Array): number {
  const t = crcTable();
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    const byte = bytes[i] ?? 0;
    c = (t[(c ^ byte) & 0xff] ?? 0) ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

interface Entry {
  nameBytes: Uint8Array;
  data: Uint8Array;
  crc: number;
  offset: number;
}

export interface ZipFile {
  name: string;
  content: string;
}

/** Build a store-only ZIP archive from a set of text files. */
export function buildZip(files: readonly ZipFile[]): Uint8Array {
  const chunks: Uint8Array[] = [];
  const entries: Entry[] = [];
  let offset = 0;

  const push = (b: Uint8Array) => {
    chunks.push(b);
    offset += b.length;
  };

  // Local file headers + data.
  for (const f of files) {
    const nameBytes = textEncoder.encode(f.name);
    const data = textEncoder.encode(f.content);
    const crc = crc32(data);
    const header = new Uint8Array(30 + nameBytes.length);
    const dv = new DataView(header.buffer);
    dv.setUint32(0, 0x04034b50, true); // local file header signature
    dv.setUint16(4, 20, true); // version needed
    dv.setUint16(6, 0x0800, true); // flags: UTF-8 filename
    dv.setUint16(8, 0, true); // method: store
    dv.setUint16(10, 0, true); // mod time (00:00:00)
    dv.setUint16(12, 0x0021, true); // mod date (1980-01-01, a valid DOS date)
    dv.setUint32(14, crc, true);
    dv.setUint32(18, data.length, true); // compressed size
    dv.setUint32(22, data.length, true); // uncompressed size
    dv.setUint16(26, nameBytes.length, true);
    dv.setUint16(28, 0, true); // extra len
    header.set(nameBytes, 30);

    entries.push({ nameBytes, data, crc, offset });
    push(header);
    push(data);
  }

  // Central directory.
  const cdStart = offset;
  for (const e of entries) {
    const rec = new Uint8Array(46 + e.nameBytes.length);
    const dv = new DataView(rec.buffer);
    dv.setUint32(0, 0x02014b50, true); // central dir signature
    dv.setUint16(4, 20, true); // version made by
    dv.setUint16(6, 20, true); // version needed
    dv.setUint16(8, 0x0800, true); // flags: UTF-8
    dv.setUint16(10, 0, true); // method: store
    dv.setUint16(12, 0, true); // mod time (00:00:00)
    dv.setUint16(14, 0x0021, true); // mod date (1980-01-01, a valid DOS date)
    dv.setUint32(16, e.crc, true);
    dv.setUint32(20, e.data.length, true);
    dv.setUint32(24, e.data.length, true);
    dv.setUint16(28, e.nameBytes.length, true);
    dv.setUint16(30, 0, true); // extra len
    dv.setUint16(32, 0, true); // comment len
    dv.setUint16(34, 0, true); // disk number start
    dv.setUint16(36, 0, true); // internal attrs
    dv.setUint32(38, 0, true); // external attrs
    dv.setUint32(42, e.offset, true); // local header offset
    rec.set(e.nameBytes, 46);
    push(rec);
  }
  const cdSize = offset - cdStart;

  // End of central directory.
  const eocd = new Uint8Array(22);
  const dv = new DataView(eocd.buffer);
  dv.setUint32(0, 0x06054b50, true);
  dv.setUint16(4, 0, true); // disk number
  dv.setUint16(6, 0, true); // cd start disk
  dv.setUint16(8, entries.length, true); // entries on this disk
  dv.setUint16(10, entries.length, true); // total entries
  dv.setUint32(12, cdSize, true);
  dv.setUint32(16, cdStart, true);
  dv.setUint16(20, 0, true); // comment len
  push(eocd);

  // Concatenate.
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(total);
  let p = 0;
  for (const c of chunks) {
    out.set(c, p);
    p += c.length;
  }
  return out;
}
